import { getCalendarEvents } from "@rpforms/shared/build/actions/calendar_events";
import { getCalendarEventParticipants } from "@rpforms/shared/build/actions/calendar_event_participants";
import { fetchEmployees } from "@rpforms/shared/build/actions/employees";
import { getDevices } from "@rpforms/shared/build/actions/devices";
import moment from "moment";
import "moment/locale/de";
import React, { useEffect, useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../reducers";
import styled from "styled-components";

const ReportContainer = styled.div.attrs({ className: "schedule-container" })`
    padding: 12px 6px 0 6px;
`;

const ReportHeader = styled.div.attrs({ className: "schedule-header" })`
    text-align: center;
    margin-bottom: 20px;
`;

const ReportTitle = styled.div.attrs({ className: "schedule-title" })`
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 8px;
`;

const GroupCard = styled.div.attrs({ className: "group-card" })`
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    margin-bottom: 16px;
    overflow: hidden;
`;

const GroupHeader = styled.div.attrs({ className: "group-header" })`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: #f0f4f8;
    font-weight: bold;
    font-size: 16px;
    border-bottom: 1px solid #e0e0e0;
`;

const GroupBody = styled.div.attrs({ className: "group-body" })`
    padding: 10px 14px;
`;

const ItemRow = styled.div.attrs({ className: "item-row" })`
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

const ItemTitle = styled.div.attrs({ className: "item-title" })`
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 4px;
`;

const ItemMeta = styled.div.attrs({ className: "item-meta" })`
    color: #666;
    font-size: 13px;
    margin-top: 2px;
`;

const ItemNotes = styled.div.attrs({ className: "item-notes" })`
    color: #333;
    font-size: 13px;
    margin-top: 4px;
    white-space: pre-wrap;
`;

const ItemTime = styled.div.attrs({ className: "item-time" })`
    color: #2563eb;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 4px;
`;

const DatePickerWrapper = styled.div`
    padding: 20px;
    text-align: center;
`;

const EmptyState = styled.div`
    padding: 40px 20px;
    color: #666;
    text-align: center;
`;

interface IDailyScheduleModalProps {
    show: boolean;
    onHide: () => void;
}

interface ScheduleEvent {
    id: number;
    name: string;
    scheduled_from: string;
    scheduled_to: string;
    device_id: number;
    device_name?: string;
    deviceTasks?: any[];
    participantNames?: string;
    offerTitle?: string;
    notes?: string;
}

interface DeviceGroup {
    deviceId: number;
    deviceName: string;
    events: ScheduleEvent[];
}

const DailyScheduleModal: React.FC<IDailyScheduleModalProps> = ({ show, onHide }) => {
    const dispatch = useDispatch();
    const devices = useSelector<RootState, any>((state) => state.device.devices);
    const employees = useSelector<RootState, any>((state) => state.employees.employees);
    const allParticipants = useSelector<RootState, any>(
        (store) => store.calendarEventParticipants.participants
    );
    const calendarEvents = useSelector<RootState, any>(
        (state) => state.calendarEvents.calendarEvents
    );
    const calendarEventsLoading = useSelector<RootState, boolean>(
        (state) => state.calendarEvents.isLoading
    );

    const [selectedDate, setSelectedDate] = useState<string>(
        moment().add(1, "day").format("YYYY-MM-DD")
    );
    const [scheduleData, setScheduleData] = useState<DeviceGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [reportGenerated, setReportGenerated] = useState(false);

    useEffect(() => {
        if (show) {
            dispatch(getDevices({ forSelect: true }));
            dispatch(fetchEmployees());
            dispatch(getCalendarEventParticipants());
        }
    }, [show, dispatch]);

    useEffect(() => {
        if (reportGenerated && !calendarEventsLoading && calendarEvents) {
            processEvents(calendarEvents);
        }
    }, [
        calendarEvents,
        calendarEventsLoading,
        reportGenerated,
        devices,
        employees,
        allParticipants,
    ]);

    const processEvents = (events: any[]) => {
        try {
            const dayStart = moment(selectedDate).startOf("day");
            const dayEnd = moment(selectedDate).endOf("day");

            const processedEvents: ScheduleEvent[] = events
                .filter((event: any) => {
                    if (event.employee_id) {
                        return false;
                    }

                    if (event.was_submitted) {
                        return false;
                    }

                    if (!event.device_tasks || event.device_tasks.length === 0) {
                        return false;
                    }

                    const eventStart = moment(event.scheduled_from);
                    const eventEnd = moment(event.scheduled_to);

                    return eventStart.isSameOrBefore(dayEnd) && eventEnd.isSameOrAfter(dayStart);
                })
                .map((event: any) => {
                    const device = devices.find((d: any) => d.id === event.device_id);

                    let offerTitle: string | undefined;
                    if (event.device_tasks && event.device_tasks.length > 0) {
                        const offerTask = event.device_tasks.find((dt: any) => dt.form_entry?.name);
                        if (offerTask) {
                            offerTitle = offerTask.form_entry.name;
                        }
                    }

                    let participantNames: string | undefined;
                    if (allParticipants && allParticipants.length > 0) {
                        const eventParticipants = allParticipants
                            .filter((p: any) => p.calendar_event_id === event.id)
                            .map((p: any) => p.employee_id);

                        const participantNamesList = employees
                            .filter((e: any) => eventParticipants.includes(e.id))
                            .map((e: any) => e.name)
                            .join(", ");

                        participantNames = participantNamesList || undefined;
                    }

                    let eventTitle = event.name || event.calendar_title || "Unbenannt";
                    if (
                        offerTitle &&
                        !eventTitle.toLowerCase().includes(offerTitle.toLowerCase())
                    ) {
                        eventTitle = `${eventTitle} (${offerTitle})`;
                    }

                    return {
                        id: event.id,
                        name: eventTitle,
                        scheduled_from: event.scheduled_from,
                        scheduled_to: event.scheduled_to,
                        device_id: event.device_id,
                        device_name: device?.name || "Ohne Gerät",
                        deviceTasks: event.deviceTasks,
                        participantNames,
                        offerTitle,
                        notes: event.notes?.trim() || undefined,
                    };
                });

            const groupedByDevice = processedEvents.reduce((groups: DeviceGroup[], event) => {
                const deviceId = event.device_id || 0;
                const existingGroup = groups.find((g) => g.deviceId === deviceId);

                if (existingGroup) {
                    existingGroup.events.push(event);
                } else {
                    groups.push({
                        deviceId,
                        deviceName: event.device_name || "Ohne Gerät",
                        events: [event],
                    });
                }
                return groups;
            }, []);

            groupedByDevice.sort((a, b) => {
                if (a.deviceId === 0) return 1;
                if (b.deviceId === 0) return -1;
                return a.deviceName.localeCompare(b.deviceName);
            });

            groupedByDevice.forEach((group) => {
                group.events.sort((a, b) =>
                    moment(a.scheduled_from).diff(moment(b.scheduled_from))
                );
            });

            setScheduleData(groupedByDevice);
            setLoading(false);
        } catch (error) {
            console.error("Error processing events:", error);
            setLoading(false);
        }
    };

    const formattedDate = useMemo(() => {
        return moment(selectedDate).format("dddd, D. MMMM YYYY");
    }, [selectedDate]);

    const handleGenerate = () => {
        setLoading(true);
        setReportGenerated(true);
        setScheduleData([]);

        const startOfDay = moment(selectedDate).startOf("day").format("YYYY-MM-DDTHH:mm:ssZ");
        const endOfDay = moment(selectedDate).endOf("day").format("YYYY-MM-DDTHH:mm:ssZ");

        dispatch(getCalendarEvents({ start: startOfDay, end: endOfDay }));
    };

    const handlePrint = () => {
        const html = ReactDOMServer.renderToString(
            <DailySchedulePrintView scheduleData={scheduleData} formattedDate={formattedDate} />
        );

        const iframe = document.createElement("iframe");
        iframe.setAttribute("aria-hidden", "true");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) {
            document.body.removeChild(iframe);
            onHide();
            return;
        }

        doc.open();
        doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Tagesplan - ${formattedDate}</title>
    <style>
      body { margin: 0; font-family: Arial, sans-serif; color: #111; }
      .schedule-container { padding: 20px; }
      .schedule-header { text-align: center; margin-bottom: 24px; }
      .schedule-title { font-size: 24px; font-weight: bold; margin-bottom: 8px; }
      .group-card { border: 1px solid #e6e6e6; border-radius: 6px; margin-bottom: 16px; overflow: hidden; }
      .group-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f0f4f8; font-weight: bold; font-size: 16px; border-bottom: 1px solid #e0e0e0; }
      .group-body { padding: 10px 14px; }
      .item-row { padding: 10px 0; border-bottom: 1px solid #f0f0f0; }
      .item-row:last-child { border-bottom: none; }
      .item-title { font-weight: 600; font-size: 15px; margin-bottom: 4px; }
      .item-meta { color: #666; font-size: 13px; margin-top: 2px; }
      .item-notes { color: #333; font-size: 13px; margin-top: 4px; white-space: pre-wrap; }
      .item-time { color: #2563eb; font-weight: 600; font-size: 14px; margin-bottom: 4px; }
    </style>
  </head>
  <body>${html}</body>
</html>`);
        doc.close();

        const iframeWindow = iframe.contentWindow;
        const cleanup = () => {
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
        };

        const parentFocusCleanup = () => {
            window.removeEventListener("focus", parentFocusCleanup);
            cleanup();
        };

        window.addEventListener("focus", parentFocusCleanup, { once: true });

        if (iframeWindow) {
            iframeWindow.onafterprint = cleanup;
            iframeWindow.focus();
            iframeWindow.print();
        }
    };

    const handleClose = () => {
        setReportGenerated(false);
        setLoading(false);
        setScheduleData([]);
        onHide();
    };

    const renderScheduleReport = () => {
        if (scheduleData.length === 0) {
            return <EmptyState>Keine Termine für diesen Tag vorhanden.</EmptyState>;
        }

        return (
            <>
                {scheduleData.map((group) => (
                    <GroupCard key={group.deviceId}>
                        <GroupHeader>
                            <span>{group.deviceName}</span>
                            <span>
                                {group.events.length} Termin{group.events.length !== 1 ? "e" : ""}
                            </span>
                        </GroupHeader>
                        <GroupBody>
                            {group.events.map((event) => (
                                <ItemRow key={event.id}>
                                    <ItemTime>
                                        {moment(event.scheduled_from).format("HH:mm")} -{" "}
                                        {moment(event.scheduled_to).format("HH:mm")}
                                    </ItemTime>
                                    <ItemTitle>{event.name}</ItemTitle>
                                    {event.participantNames && (
                                        <ItemMeta>Mitarbeiter: {event.participantNames}</ItemMeta>
                                    )}
                                    {event.offerTitle &&
                                        !event.name
                                            .toLowerCase()
                                            .includes(event.offerTitle.toLowerCase()) && (
                                            <ItemMeta>Angebot: {event.offerTitle}</ItemMeta>
                                        )}
                                    {event.notes && <ItemNotes>{event.notes}</ItemNotes>}
                                </ItemRow>
                            ))}
                        </GroupBody>
                    </GroupCard>
                ))}
            </>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" className="daily-schedule-modal">
            <Modal.Header closeButton>
                <Modal.Title>Tagesplan generieren</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!reportGenerated ? (
                    <DatePickerWrapper>
                        <p>Wählen Sie ein Datum für den Tagesplan:</p>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="form-control"
                            style={{ maxWidth: "250px", margin: "0 auto" }}
                        />
                        <p style={{ marginTop: "10px", color: "#666", fontSize: "13px" }}>
                            Standard: Morgen
                        </p>
                    </DatePickerWrapper>
                ) : loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Laden...</span>
                        </Spinner>
                        <div className="mt-2">Tagesplan wird geladen...</div>
                    </div>
                ) : (
                    <ReportContainer>
                        <ReportHeader>
                            <ReportTitle>{formattedDate}</ReportTitle>
                        </ReportHeader>
                        {renderScheduleReport()}
                    </ReportContainer>
                )}
            </Modal.Body>
            <Modal.Footer>
                {!reportGenerated ? (
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            Abbrechen
                        </Button>
                        <Button variant="primary" onClick={handleGenerate}>
                            Generieren
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setReportGenerated(false);
                                setLoading(false);
                                setScheduleData([]);
                            }}
                        >
                            Zurück
                        </Button>
                        <Button variant="primary" onClick={handlePrint}>
                            Drucken
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

const DailySchedulePrintView = ({
    scheduleData,
    formattedDate,
}: {
    scheduleData: DeviceGroup[];
    formattedDate: string;
}) => {
    return (
        <div className="daily-schedule-print-view">
            <ReportContainer>
                <ReportHeader>
                    <ReportTitle>{formattedDate}</ReportTitle>
                </ReportHeader>
                {scheduleData.length === 0 ? (
                    <EmptyState>Keine Termine für diesen Tag vorhanden.</EmptyState>
                ) : (
                    <>
                        {scheduleData.map((group) => (
                            <GroupCard key={group.deviceId}>
                                <GroupHeader>
                                    <span>{group.deviceName}</span>
                                    <span>
                                        {group.events.length} Termin
                                        {group.events.length !== 1 ? "e" : ""}
                                    </span>
                                </GroupHeader>
                                <GroupBody>
                                    {group.events.map((event) => (
                                        <ItemRow key={event.id}>
                                            <ItemTime>
                                                {moment(event.scheduled_from).format("HH:mm")} -{" "}
                                                {moment(event.scheduled_to).format("HH:mm")}
                                            </ItemTime>
                                            <ItemTitle>{event.name}</ItemTitle>
                                            {event.participantNames && (
                                                <ItemMeta>
                                                    Mitarbeiter: {event.participantNames}
                                                </ItemMeta>
                                            )}
                                            {event.offerTitle &&
                                                !event.name
                                                    .toLowerCase()
                                                    .includes(event.offerTitle.toLowerCase()) && (
                                                    <ItemMeta>Angebot: {event.offerTitle}</ItemMeta>
                                                )}
                                            {event.notes && <ItemNotes>{event.notes}</ItemNotes>}
                                        </ItemRow>
                                    ))}
                                </GroupBody>
                            </GroupCard>
                        ))}
                    </>
                )}
            </ReportContainer>
        </div>
    );
};

export default DailyScheduleModal;
